import { type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AuthProvider, useAuth } from './auth/AuthProvider'
import { homePathForUser } from './auth/routing'
import { CartProvider } from './cart/CartProvider'
import { ActiveVenueProvider } from './venues/ActiveVenueProvider'
import { AppShell } from './components/AppShell'
import { OperatorShell } from './components/OperatorShell'
import { ToastProvider } from './components/ToastProvider'
import { isPivotModeEnabled } from './pivot/pivotMode'
import { isDemoStandEnabled } from './dev/demoStand'
import { CartScreen } from './screens/CartScreen'
import { CatalogScreen } from './screens/CatalogScreen'
import { ChatScreen } from './screens/ChatScreen'
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen'
import { ResetPasswordScreen } from './screens/ResetPasswordScreen'
import { LoginScreen, RequireAuth } from './screens/LoginScreen'
import { RegisterScreen } from './screens/RegisterScreen'
import { PersonalDataConsentLegalScreen } from './screens/PersonalDataConsentLegalScreen'
import { NotificationsConsentLegalScreen } from './screens/NotificationsConsentLegalScreen'
import { PublicOfferLegalScreen } from './screens/PublicOfferLegalScreen'
import { RestaurantOverviewScreen } from './screens/RestaurantOverviewScreen'
import { NotificationsScreen } from './screens/NotificationsScreen'
import { OperatorCatalogReviewScreen } from './screens/OperatorCatalogReviewScreen'
import {
  OperatorRestaurantOrganizationScreen,
  OperatorSupplierOrganizationScreen,
} from './screens/OperatorOrganizationScreen'
import { OperatorRestaurantPricesScreen } from './screens/OperatorRestaurantPricesScreen'
import { OperatorRestaurantRfqsScreen } from './screens/OperatorRestaurantRfqsScreen'
import { OperatorRestaurantsScreen } from './screens/OperatorRestaurantsScreen'
import { OperatorRfqScreen } from './screens/OperatorRfqScreen'
import { OperatorSuppliersScreen } from './screens/OperatorSuppliersScreen'
import { OrdersScreen } from './screens/OrdersScreen'
import { RequestScreen } from './screens/RequestScreen'
import { RequireMarketUser } from './screens/RequireMarketUser'
import { RequireOperator } from './screens/RequireOperator'
import { RfqCreateScreen } from './screens/RfqCreateScreen'
import { PaymentsScreen } from './screens/PaymentsScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { SupplierCatalogImportScreen } from './screens/SupplierCatalogImportScreen'
import { SupplierCatalogScreen } from './screens/SupplierCatalogScreen'
import { RestaurantMyPricesScreen } from './screens/RestaurantMyPricesScreen'
import { SupplierCustomersScreen } from './screens/SupplierCustomersScreen'
import { SupplierOverviewScreen } from './screens/SupplierOverviewScreen'
import { SuppliersScreen } from './screens/SuppliersScreen'
import { MarketAnalyticsScreen } from './screens/MarketAnalyticsScreen'

const HomeRedirect = () => {
  const { userRoles } = useAuth()
  return <Navigate to={homePathForUser(userRoles)} replace />
}

/** Hide marketplace / supplier cabinet surfaces while pivot is on (routes kept for later). */
const PivotRedirect = ({ buyerTo, supplierTo = '/settings' }: { buyerTo: string; supplierTo?: string }) => {
  const { role } = useAuth()
  if (role === 'supplier') {
    return <Navigate to={supplierTo} replace />
  }
  return <Navigate to={buyerTo} replace />
}

const WhenNotPivot = ({ children }: { children: ReactNode }) => {
  if (isPivotModeEnabled()) {
    return <PivotRedirect buyerTo="/my-prices" />
  }
  return children
}

const OverviewRoute = () => {
  const { role } = useAuth()
  if (isPivotModeEnabled()) {
    return <PivotRedirect buyerTo="/my-prices" />
  }
  if (role === 'supplier') {
    return <SupplierOverviewScreen />
  }
  return <RestaurantOverviewScreen />
}

const CatalogRoute = () => {
  const { role } = useAuth()
  if (isPivotModeEnabled()) {
    return <PivotRedirect buyerTo="/my-prices" />
  }
  if (role === 'supplier') {
    return <SupplierCatalogScreen />
  }
  return <CatalogScreen />
}

const CatalogImportRoute = () => {
  const { role } = useAuth()
  if (role === 'buyer') {
    return <Navigate to="/my-prices/import" replace />
  }
  if (isPivotModeEnabled()) {
    return <Navigate to="/settings" replace />
  }
  return <SupplierCatalogImportScreen />
}

const CartRoute = () => {
  if (isPivotModeEnabled()) {
    return <RequestScreen />
  }
  return <CartScreen />
}

const OrdersRoute = () => {
  if (isPivotModeEnabled()) {
    return <PivotRedirect buyerTo="/cart?tab=history" />
  }
  return <OrdersScreen />
}

const HideOnDemoStand = ({ children }: { children: ReactNode }) => {
  if (isDemoStandEnabled()) {
    return <Navigate to="/my-prices" replace />
  }
  return children
}

const PaymentsRoute = () => (
  <HideOnDemoStand>
    <PaymentsScreen />
  </HideOnDemoStand>
)

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginScreen />} />
    <Route path="/register" element={<RegisterScreen />} />
    <Route path="/legal/personal-data-consent" element={<PersonalDataConsentLegalScreen />} />
    <Route path="/legal/notifications-consent" element={<NotificationsConsentLegalScreen />} />
    <Route path="/legal/offer" element={<PublicOfferLegalScreen />} />
    <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
    <Route path="/reset-password" element={<ResetPasswordScreen />} />
    <Route
      element={
        <RequireAuth>
          <RequireMarketUser>
            <AppShell />
          </RequireMarketUser>
        </RequireAuth>
      }
    >
      <Route index element={<HomeRedirect />} />
      <Route path="billing/checkout" element={<HideOnDemoStand><Navigate to="/payments" replace /></HideOnDemoStand>} />
      <Route path="overview" element={<OverviewRoute />} />
      <Route path="catalog" element={<CatalogRoute />} />
      <Route path="catalog/import" element={<CatalogImportRoute />} />
      <Route path="my-prices" element={<RestaurantMyPricesScreen />} />
      <Route path="my-prices/import" element={<SupplierCatalogImportScreen />} />
      <Route path="cart" element={<CartRoute />} />
      <Route path="orders" element={<OrdersRoute />} />
      <Route
        path="orders/create"
        element={
          <WhenNotPivot>
            <RfqCreateScreen />
          </WhenNotPivot>
        }
      />
      <Route
        path="suppliers"
        element={
          <WhenNotPivot>
            <SuppliersScreen />
          </WhenNotPivot>
        }
      />
      <Route
        path="customers"
        element={
          <WhenNotPivot>
            <SupplierCustomersScreen />
          </WhenNotPivot>
        }
      />
      <Route
        path="notifications"
        element={
          <WhenNotPivot>
            <NotificationsScreen />
          </WhenNotPivot>
        }
      />
      <Route path="payments" element={<PaymentsRoute />} />
      <Route path="analytics" element={<MarketAnalyticsScreen />} />
      <Route path="settings" element={<HideOnDemoStand><SettingsScreen /></HideOnDemoStand>} />
      <Route
        path="chat"
        element={
          <WhenNotPivot>
            <ChatScreen />
          </WhenNotPivot>
        }
      />
    </Route>
    <Route
      path="/operator"
      element={
        <RequireOperator>
          <OperatorShell />
        </RequireOperator>
      }
    >
      <Route index element={<OperatorSuppliersScreen />} />
      <Route path="suppliers/:organizationId" element={<OperatorSupplierOrganizationScreen />} />
      <Route path="suppliers/:organizationId/catalog" element={<OperatorCatalogReviewScreen />} />
      <Route path="suppliers/:organizationId/catalog/import" element={<SupplierCatalogImportScreen />} />
      <Route path="buyers" element={<OperatorRestaurantsScreen />} />
      <Route path="buyers/:organizationId" element={<OperatorRestaurantOrganizationScreen />} />
      <Route path="buyers/:organizationId/prices" element={<OperatorRestaurantPricesScreen />} />
      <Route path="buyers/:organizationId/prices/import" element={<SupplierCatalogImportScreen />} />
      <Route path="buyers/:organizationId/rfqs" element={<OperatorRestaurantRfqsScreen />} />
      <Route path="rfqs/:rfqId" element={<OperatorRfqScreen />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <ActiveVenueProvider>
        <CartProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </CartProvider>
      </ActiveVenueProvider>
    </BrowserRouter>
  </AuthProvider>
)

export default App
